/**
 * section-config — narrow a `Section.config` union to the arm its `kind` implies.
 *
 * The contract types `config` as `ChartGroup | ScreenerConfig` because JSON
 * Schema states the two arms as a `oneOf` without tying either to a `kind`
 * value. TypeScript therefore can't discriminate on `section.kind` alone, so
 * the correspondence is enforced here — once, at the boundary — instead of
 * being cast away at each call site.
 */

import type { ChartGroup, DataViewConfig, ScreenerConfig, Section } from "@qufolio/model";

/**
 * The section's DataViewConfig — one data panel view as a grid cell — or
 * `undefined` if it isn't one. An unchosen cell is `{}`, never `undefined`.
 */
export function dataViewConfig(section: Section): DataViewConfig | undefined {
  return section.kind === "dataView" ? ((section.config as DataViewConfig | undefined) ?? {}) : undefined;
}

/** The section's ChartGroup config, or `undefined` if it isn't a chart group. */
export function chartGroupConfig(section: Section): ChartGroup | undefined {
  return section.kind === "chartGroup" && section.config
    ? (section.config as ChartGroup)
    : undefined;
}

/** The section's ScreenerConfig, or `undefined` if it isn't a screener. */
export function screenerConfig(section: Section): ScreenerConfig | undefined {
  return section.kind === "screener" && section.config
    ? (section.config as ScreenerConfig)
    : undefined;
}
